import React from "react";
import { FaPlus, FaTrash } from "react-icons/fa";

type TaskType =
  | "BEISPIEL_SATZ"
  | "SATZBAU"
  | "ERGANZUNGEN"
  | "HOREN"
  | "SCHREIBEN";

type CorrectAnswersInputProps = {
  taskIndex: number;
  taskType: TaskType;
  correctAnswer: string;
  onUpdate: (value: string) => void;
};

const CorrectAnswersInput: React.FC<CorrectAnswersInputProps> = ({
  taskIndex,
  taskType,
  correctAnswer,
  onUpdate,
}) => {
  // ✅ Parse existing answers - supports both JSON array and old format
  const [answers, setAnswers] = React.useState<string[]>(() => {
    if (!correctAnswer || correctAnswer.trim() === "") return [""];

    try {
      // ✅ NEW: Try parsing as JSON array first
      if (correctAnswer.trim().startsWith("[")) {
        const parsed = JSON.parse(correctAnswer);
        return parsed.length > 0 ? parsed : [""];
      }
    } catch (e) {
      // Not JSON, fall through to old format
      console.warn("Failed to parse as JSON, using legacy format");
    }

    // ✅ FALLBACK: Legacy format
    const parsed = correctAnswer
      .split("\n")
      .map((line) => line.replace(/^\d+\.\s*/, "").trim())
      .filter((line) => line !== "");

    return parsed.length > 0 ? parsed : [""];
  });

  // ✅ Update parent with JSON array format
  React.useEffect(() => {
    const filtered = answers.filter((ans) => ans.trim() !== "");

    if (filtered.length === 0) {
      onUpdate("");
      return;
    }

    // Create JSON array of answers
    const combined = JSON.stringify(filtered);

    // Only update if value actually changed
    if (combined !== correctAnswer) {
      onUpdate(combined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers]); // Only depend on answers array

  const addAnswer = () => {
    setAnswers([...answers, ""]);
  };

  const removeAnswer = (idx: number) => {
    if (answers.length === 1) {
      alert("At least one answer is required");
      return;
    }
    const newAnswers = answers.filter((_, i) => i !== idx);
    setAnswers(newAnswers);
  };

  const updateAnswer = (idx: number, value: string) => {
    const newAnswers = [...answers];
    newAnswers[idx] = value;
    setAnswers(newAnswers);
  };

  return (
    <div className="space-y-3">
      {answers.map((answer, idx) => (
        <div key={idx} className="flex items-start gap-2">
          <div className="flex-shrink-0 w-8 h-10 flex items-center justify-center bg-[#009fe3] text-white rounded-lg font-bold text-sm">
            {idx + 1}
          </div>
          <input
            type="text"
            value={answer}
            onChange={(e) => updateAnswer(idx, e.target.value)}
            placeholder={`Answer ${idx + 1}`}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent transition-all"
            style={{ fontSize: "16px" }}
            required
          />
          {answers.length > 1 && (
            <button
              type="button"
              onClick={() => removeAnswer(idx)}
              className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 hover:bg-red-200 rounded-lg transition-colors"
              title="Remove this answer"
            >
              <FaTrash size={14} />
            </button>
          )}
        </div>
      ))}

      <button
        type="button"
        onClick={addAnswer}
        className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm font-semibold"
      >
        <FaPlus size={12} />
        Add Answer
      </button>

      {/* Preview */}
      <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-xs font-semibold text-gray-700 mb-2">
          Preview (How it will be stored):
        </p>
        <pre className="text-xs text-gray-600 font-mono whitespace-pre-wrap">
          {answers.filter((ans) => ans.trim() !== "").length > 0
            ? JSON.stringify(
                answers.filter((ans) => ans.trim() !== ""),
                null,
                2
              )
            : "(No answers yet)"}
        </pre>
      </div>
    </div>
  );
};

export default CorrectAnswersInput;
